import type { Rect } from "./input";
import type { GamePhase } from "./types";

const BASE_EDGE_PX = 24;
const GAMEPLAY_FALLBACK_LEFT_EDGE_PX = 64;
const GAMEPLAY_LEFT_GUARD_TILES = 1.5;
const BOARD_TILE_COUNT = 10;

export interface EdgeSwipeGuardInput {
  phase: GamePhase;
  touchClientX: number;
  viewportWidth: number;
  boardRect?: Pick<Rect, "x" | "w"> | null;
  canvasRect?: { left: number; width: number } | null;
  logicalCanvasWidth?: number | null;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isPositiveFiniteNumber(value: number | null | undefined): value is number {
  return isFiniteNumber(value) && value > 0;
}

function resolveBoardLeftEdgePx(
  input: EdgeSwipeGuardInput,
  fallbackLeftEdgePx: number,
  leftGuardTiles: number
): number {
  const boardRect = input.boardRect;
  const canvasRect = input.canvasRect;
  const logicalCanvasWidth = input.logicalCanvasWidth;
  if (
    boardRect == null
    || canvasRect == null
    || !isFiniteNumber(boardRect.x)
    || !isPositiveFiniteNumber(boardRect.w)
    || !isFiniteNumber(canvasRect.left)
    || !isPositiveFiniteNumber(canvasRect.width)
    || !isPositiveFiniteNumber(logicalCanvasWidth)
  ) {
    return fallbackLeftEdgePx;
  }

  const scaleX = canvasRect.width / logicalCanvasWidth;
  const tileWidth = boardRect.w / BOARD_TILE_COUNT;
  const leftGuardPx = canvasRect.left + (boardRect.x + tileWidth * leftGuardTiles) * scaleX;
  return Math.max(BASE_EDGE_PX, leftGuardPx);
}

function usesGameplayBoardEdgeGuard(phase: GamePhase): boolean {
  return phase === "RUN_PLAYABLE"
    || phase === "RUN_RESOLVING"
    || phase === "RUN_LEVELUP"
    || phase === "VERSUS_MATCH";
}

export function shouldBlockEdgeSwipeStart(input: EdgeSwipeGuardInput): boolean {
  if (input.touchClientX > input.viewportWidth - BASE_EDGE_PX) {
    return true;
  }

  let leftEdgePx = BASE_EDGE_PX;
  if (usesGameplayBoardEdgeGuard(input.phase)) {
    leftEdgePx = resolveBoardLeftEdgePx(
      input,
      GAMEPLAY_FALLBACK_LEFT_EDGE_PX,
      GAMEPLAY_LEFT_GUARD_TILES
    );
  }
  return input.touchClientX < leftEdgePx;
}
